export type QuoteStatus = 'draft' | 'sent' | 'accepted' | 'rejected' | 'expired' | 'replaced';
export type InvoiceStatus = 'unpaid' | 'partial' | 'paid';
export type PaymentMethod = 'bank_transfer' | 'cash' | 'cheque' | 'other';
export type AcceptanceMethod = 'signed_document' | 'email' | 'line' | 'phone' | 'other';
export type Plan = 'free' | 'pro' | 'business';
export type SyncState = 'local' | 'pending' | 'synced' | 'conflict';

export interface AccountProfile {
  userId: string;
  displayName: string;
  plan: Plan;
  systemRole: 'customer' | 'support' | 'admin';
  accountStatus: 'active' | 'suspended';
  cloudSyncEnabled: boolean;
  onboardingCompleted: boolean;
  termsAcceptedAt: string | null;
  planExpiresAt: string | null;
}

export interface LineItem {
  id: string;
  description: string;
  quantity: number;
  unit: string;
  unitPrice: number;
}

export interface Quote {
  id: string;
  number: string;
  customerType: 'company' | 'individual';
  customerName: string;
  customerTaxId: string;
  customerBranchType: 'headOffice' | 'branch';
  customerBranchCode: string;
  customerContactName: string;
  customerEmail: string;
  customerPhone: string;
  customerAddress: string;
  issueDate: string;
  expiryDate: string;
  status: QuoteStatus;
  items: LineItem[];
  discountType: 'amount' | 'percent';
  discount: number;
  taxRate: number;
  withholdingTaxRate: number;
  notes: string;
  acceptance?: QuoteAcceptance | null;
  showProviderSignature?: boolean;
  showSignatureSection?: boolean;
  revision?: number;
  revisedFromId?: string | null;
  replacedById?: string | null;
  createdAt: string;
  updatedAt: string;
  version: number;
  deletedAt: string | null;
  syncState: SyncState;
  cloudRevision?: number;
  trashExpiresAt?: string | null;
  purgedAt?: string | null;
  trashLegacy?: boolean;
  localTrashOnly?:boolean;
}

export interface QuoteAcceptance {
  acceptedAt: string;
  acceptedBy: string;
  acceptedByRole: string;
  method: AcceptanceMethod;
  reference: string;
  note: string;
  evidence: PaymentAttachment | null;
  signature: PaymentAttachment | null;
  recordedAt: string;
  quoteVersion: number;
  confirmed: true;
}

export interface Invoice {
  id: string;
  number: string;
  quoteId: string;
  quoteSnapshot: Quote;
  issueDate: string;
  dueDate: string;
  status: InvoiceStatus;
  createdAt: string;
  updatedAt: string;
  version: number;
  deletedAt: string | null;
  syncState: SyncState;
  cloudRevision?: number;
  trashExpiresAt?: string | null;
  purgedAt?: string | null;
  trashLegacy?: boolean;
  localTrashOnly?:boolean;
}

export interface PaymentAttachment {
  name: string;
  type: string;
  size: number;
  dataUrl: string;
}

export interface Payment {
  id: string;
  invoiceId: string;
  receiptNumber: string;
  paidAt: string;
  amount: number;
  method: PaymentMethod;
  reference: string;
  note: string;
  attachment: PaymentAttachment | null;
  createdAt: string;
  voidedAt: string | null;
}

export interface CustomerProfile {
  type: Quote['customerType'];
  name: string;
  taxId: string;
  branchType: Quote['customerBranchType'];
  branchCode: string;
  contactName: string;
  email: string;
  phone: string;
  address: string;
}

export interface CompanyProfile {
  type: 'company' | 'individual';
  name: string;
  brandName: string;
  logoDataUrl: string;
  taxId: string;
  branchType: 'headOffice' | 'branch';
  branchCode: string;
  contactName: string;
  address: string;
  email: string;
  phone: string;
  bankName: string;
  bankAccount: string;
}

export interface AppSettings {
  cloudMissingFileKeys?: string[];
  cloudCatalogRevision?: number;
  key: 'app';
  company: CompanyProfile;
  cloudSyncEnabled: boolean;
  usageAnalyticsEnabled: boolean;
  usageAnalyticsNoticeAcknowledged: boolean;
  lastSyncAt: string | null;
  cloudSettingsUpdatedAt: string | null;
  lastBackupAt: string | null;
  backupReminderDismissedAt: string | null;
  quoteSequenceYear: number;
  lastQuoteSequence: number;
  invoiceSequenceYear: number;
  lastInvoiceSequence: number;
  receiptSequenceYear: number;
  lastReceiptSequence: number;
}

export const defaultCompany: CompanyProfile = {
  type: 'company',
  name: 'บริษัทของคุณ',
  brandName: '',
  logoDataUrl: '',
  taxId: '',
  branchType: 'headOffice',
  branchCode: '',
  contactName: '',
  address: '',
  email: '',
  phone: '',
  bankName: '',
  bankAccount: '',
};

export const createDefaultSettings = (): AppSettings => ({
  key: 'app',
  company: defaultCompany,
  cloudSyncEnabled: false,
  usageAnalyticsEnabled: false,
  usageAnalyticsNoticeAcknowledged: false,
  lastSyncAt: null,
  cloudSettingsUpdatedAt: null,
  lastBackupAt: null,
  backupReminderDismissedAt: null,
  quoteSequenceYear: new Date().getFullYear(),
  lastQuoteSequence: 0,
  invoiceSequenceYear: new Date().getFullYear(),
  lastInvoiceSequence: 0,
  receiptSequenceYear: new Date().getFullYear(),
  lastReceiptSequence: 0,
});

export const roundMoney = (value: number) => Math.round((Number(value || 0) + Number.EPSILON) * 100) / 100;

export const quoteSubtotal = (quote: Pick<Quote, 'items'>) =>
  roundMoney(quote.items.reduce((sum, item) => sum + roundMoney(Number(item.quantity || 0) * Number(item.unitPrice || 0)), 0));

export const quoteDiscountAmount = (quote: Pick<Quote, 'items' | 'discount' | 'discountType'>) => {
  const subtotal = quoteSubtotal(quote);
  const discount = Math.max(0, Number(quote.discount || 0));
  return roundMoney(quote.discountType === 'percent'
    ? subtotal * Math.min(discount, 100) / 100
    : Math.min(discount, subtotal));
};

export const quoteWithholdingAmount = (quote: Pick<Quote, 'items' | 'discount' | 'discountType' | 'withholdingTaxRate'>) => {
  const base = Math.max(0, quoteSubtotal(quote) - quoteDiscountAmount(quote));
  const rate = Math.min(Math.max(0, Number(quote.withholdingTaxRate || 0)), 100);
  return roundMoney(base * rate / 100);
};

export const quoteTotal = (quote: Pick<Quote, 'items' | 'discount' | 'discountType' | 'taxRate' | 'withholdingTaxRate'>) => {
  const afterDiscount = Math.max(0, quoteSubtotal(quote) - quoteDiscountAmount(quote));
  const taxRate = Math.min(Math.max(0, Number(quote.taxRate || 0)), 100);
  const vat = roundMoney((afterDiscount * taxRate) / 100);
  return roundMoney(afterDiscount + vat - quoteWithholdingAmount(quote));
};

export const invoicePaidAmount = (invoiceId: string, payments: Payment[]) => roundMoney(
  payments.filter((payment) => payment.invoiceId === invoiceId && !payment.voidedAt)
    .reduce((sum, payment) => sum + Number(payment.amount || 0), 0),
);

export const invoiceBalance = (invoice: Invoice, payments: Payment[]) => roundMoney(
  Math.max(0, quoteTotal(invoice.quoteSnapshot) - invoicePaidAmount(invoice.id, payments)),
);

export const invoiceStatus = (invoice: Invoice, payments: Payment[]): InvoiceStatus => {
  const total = quoteTotal(invoice.quoteSnapshot);
  const paid = invoicePaidAmount(invoice.id, payments);
  if (paid <= 0) return 'unpaid';
  if (paid + 0.001 >= total) return 'paid';
  return 'partial';
};
